import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export function PlanTab({ clientId, user, kind, label }) {
  const [plan, setPlan] = useState({ title: "", content: "", updated_at: null, version: 0 });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: "", content: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/clients/${clientId}/${kind}-plan`); setPlan(data); setDraft({ title: data.title, content: data.content }); }
    catch { toast.error("Failed to load plan"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, kind]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/clients/${clientId}/${kind}-plan`, draft);
      setPlan(data); setEditing(false);
      toast.success(`${label} updated`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    setSaving(false);
  };

  const canEdit = user.role === "trainer";

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-display text-3xl uppercase">{label}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">
            {plan.updated_at ? `v${plan.version} · Updated ${new Date(plan.updated_at).toLocaleString()}` : "Not set yet"}
          </div>
        </div>
        {canEdit && !editing && (
          <Button data-testid={`edit-${kind}-btn`} onClick={() => setEditing(true)} className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-10 text-xs">
            {plan.content ? "Edit Plan" : "Create Plan"}
          </Button>
        )}
      </div>

      {editing ? (
        <div className="border border-zinc-800 p-6 space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">Title</label>
            <Input data-testid={`${kind}-title-input`} value={draft.title} onChange={(e) => setDraft({...draft, title: e.target.value})} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-12" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">Plan</label>
            <Textarea data-testid={`${kind}-content-input`} rows={18} value={draft.content} onChange={(e) => setDraft({...draft, content: e.target.value})}
              placeholder={kind === "meal" ? "Meal 1 — Breakfast\n- 6 egg whites\n- 1 cup oats\n..." : "Day 1 — Push\n- Bench Press 4x6\n- OHP 3x8\n..."}
              className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white font-mono text-sm" />
          </div>
          <div className="flex gap-2">
            <Button data-testid={`${kind}-save`} onClick={save} disabled={saving} className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-11 text-xs">{saving ? "Saving..." : "Save Plan"}</Button>
            <Button onClick={() => { setEditing(false); setDraft({ title: plan.title, content: plan.content }); }} variant="outline" className="rounded-none border-zinc-800 bg-transparent text-xs uppercase tracking-[0.2em] hover:bg-zinc-900 hover:text-white">Cancel</Button>
          </div>
        </div>
      ) : plan.content ? (
        <div className="border border-zinc-800">
          <div className="border-b border-zinc-800 p-5 bg-[#121212]">
            <div className="font-display text-2xl uppercase">{plan.title}</div>
          </div>
          <pre data-testid={`${kind}-content-display`} className="p-6 whitespace-pre-wrap font-mono text-sm leading-7 text-zinc-200">{plan.content}</pre>
        </div>
      ) : (
        <div className="border border-dashed border-zinc-800 p-16 text-center text-zinc-500 text-sm">
          No {label.toLowerCase()} yet. {canEdit ? "Click Create Plan to add one." : "Your coach will share this soon."}
        </div>
      )}
    </div>
  );
}

export function ProtocolTab({ clientId, user, ptype, label }) {
  const [doc, setDoc] = useState({ content: "", updated_at: null });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/clients/${clientId}/protocols/${ptype}`); setDoc(data); setDraft(data.content); }
    catch { toast.error("Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, ptype]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/clients/${clientId}/protocols/${ptype}`, { content: draft });
      setDoc(data); setEditing(false);
      toast.success(`${label} saved`);
    } catch { toast.error("Failed"); }
    setSaving(false);
  };
  const canEdit = user.role === "trainer";
  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-display text-3xl uppercase">{label}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">{doc.updated_at ? `Updated ${new Date(doc.updated_at).toLocaleString()}` : "Not set yet"}</div>
        </div>
        {canEdit && !editing && (
          <Button data-testid={`edit-${ptype}-btn`} onClick={() => setEditing(true)} className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-10 text-xs">
            {doc.content ? "Edit" : "Create"}
          </Button>
        )}
      </div>
      {editing ? (
        <div className="border border-zinc-800 p-6 space-y-4">
          <Textarea data-testid={`${ptype}-content-input`} rows={14} value={draft} onChange={(e) => setDraft(e.target.value)} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white font-mono text-sm" />
          <div className="flex gap-2">
            <Button data-testid={`${ptype}-save`} onClick={save} disabled={saving} className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-xs uppercase tracking-[0.2em] h-11">{saving ? "Saving..." : "Save"}</Button>
            <Button onClick={() => { setEditing(false); setDraft(doc.content); }} variant="outline" className="rounded-none border-zinc-800 bg-transparent text-xs uppercase tracking-[0.2em] hover:bg-zinc-900 hover:text-white">Cancel</Button>
          </div>
        </div>
      ) : doc.content ? (
        <pre data-testid={`${ptype}-content-display`} className="border border-zinc-800 p-6 whitespace-pre-wrap font-mono text-sm leading-7 text-zinc-200">{doc.content}</pre>
      ) : (
        <div className="border border-dashed border-zinc-800 p-16 text-center text-zinc-500 text-sm">
          No {label.toLowerCase()} yet. {canEdit ? "Add it to share with your client." : "Your coach will share this soon."}
        </div>
      )}
    </div>
  );
}
